import { Alert, Divider, EditButton, Show, Space, Tag, Typography } from "@pankod/refine-antd";
import { CanAccess, IResourceComponentsProps, useOne, useShow } from "@pankod/refine-core";
import MDEditor from "@uiw/react-md-editor";
import { RenderTimelineModal, StateButtons } from "components/common";
import { RenderSocialButtons } from "components/common/socialButtons";
import { ShowUserCard } from "components/profile/showUserCard";
import dayjs from "dayjs";
import { IBaseProps } from "interfaces";
import { ITechJam, ITechJamMetadata } from "interfaces/article";
import { arrayExtensions, stringExtensions } from "lib-common";
import { DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, DATAPROVIDER_UPDATE, HELIUM_LOGO_PATH, RESOURCE_PATH, appendToAppTitle } from "scripts/site";
import { getAppTheme } from "scripts/theme";

export const TechJamShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<ITechJam>({
    dataProviderName: DATAPROVIDER_READ
  });

  const { data: metadata } = useOne<ITechJamMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.TECHJAM
  });
  const metaConfig = metadata?.data?.config;

  const { data, isLoading } = queryResult;
  const { Text } = Typography;
  const record = data?.data as ITechJam;
  appendToAppTitle(record?.name);

  return (
    <Show
      isLoading={isLoading}
      headerProps={{
        title: record?.name,
        tags: <Tag color="gold">{stringExtensions.capitalize(record?.stateManager?.state) ?? "Loading"}</Tag>,
        backIcon: true,
        extra: <>
          {record &&
            <CanAccess key={RESOURCE_PATH.TECHJAM} resource={RESOURCE_PATH.TECHJAM.toLowerCase()} action="edit" params={{ dataProviderName: DATAPROVIDER_UPDATE, id: record.id }}>
              <Space wrap>
                {metaConfig?.editableStates?.includes(record?.stateManager?.state) && <EditButton></EditButton>}
                <StateButtons {...record as IBaseProps}
                  resourceToAct={RESOURCE_PATH.TECHJAM}
                  resourceToActId={record?.id || "0"}
                  resourceToNavigate={RESOURCE_PATH.WORKLIST}
                />
                {record && <RenderTimelineModal stateManager={record?.stateManager} entityId={record?.id} entityName={RESOURCE_PATH.TECHJAM} />}
              </Space>
            </CanAccess>
          }
        </>
      }}
    >
      {record?.authorName &&
        <>
          <Text style={{ color: "GrayText" }}>Speakers: </Text>
          <Space wrap split={<Divider type="vertical" />} >
            {arrayExtensions.validateArray(record?.authorName ?? []) ? record?.authorName?.map((e, i) => <ShowUserCard key={`speaker-${i}`} id={e}></ShowUserCard>) : "None"}
          </Space>
        </>
      }
      {record?.artifactUrl &&
        <div>
          <Typography.Text style={{ color: "GrayText" }}>
            Session artifacts: <Typography.Link href={record.artifactUrl} target="_blank" rel="noopener noreferrer">Open OneDrive Folder</Typography.Link>
          </Typography.Text>
        </div>
      }
      {record?.publishedDate &&
        <div>
          <Text style={{ color: "GrayText" }}>Published on: </Text>
          <Text style={{ color: "GrayText" }}>{dayjs(record.publishedDate).format('LLLL')}</Text>
        </div>
      }
      {
        <div style={{ marginTop: 10 }}>
          <RenderSocialButtons socialElementId={record?.socialElementId} activeMode={metaConfig?.socialElement?.activeStates?.includes(record?.stateManager?.state) ?? true} />
        </div>
      }

      <Divider></Divider>
      <Alert
        message="Session Synopsis"
        description={record?.description}
        type="info"
        showIcon={false}
        style={{ marginBottom: 15 }}
      />
      {arrayExtensions.validateArray(record?.body ?? []) &&
        record?.body?.map((content, i) =>
          <div className="artcle" key={`techjam-${i}`}>
            {content.isImageAdded && content.alignment === "ImageAboveParagraph" &&
              <div className="img-cntnr">
                <img
                  key={`techjamimg-${i}`}
                  alt={record?.name}
                  src={record?.articleImages?.find(img => img.uid === content.paraImage)?.url || HELIUM_LOGO_PATH}
                />
                {content.imageTitle && <Typography.Text style={{ color: "GrayText" }}>{content.imageTitle}</Typography.Text>}
              </div>
            }
            <div className="container" key={`para-${i}`} data-color-mode={getAppTheme()}>
              <MDEditor.Markdown source={content.paragraph} />
            </div>
            {content.isImageAdded && content.alignment === "ImageBelowParagraph" &&
              <div className="img-cntnr">
                <img
                  key={`techjamimg-${i}`}
                  alt={record?.name}
                  src={record?.articleImages?.find(img => img.uid === content.paraImage)?.url || HELIUM_LOGO_PATH}
                />
                {content.imageTitle && <Typography.Text style={{ color: "GrayText" }}>{content.imageTitle}</Typography.Text>}
              </div>
            }
          </div>
        )
      }
      <Divider></Divider>
      {arrayExtensions.validateArray(record?.tagList) &&
        <>
          <Space wrap>
            <Typography.Title level={5}>Tags:</Typography.Title>
            {arrayExtensions.validateArray(record?.tagList ?? []) ? record?.tagList?.map((e, i) => <Tag key={`tag-${i}`}>{e}</Tag>) : ""}
          </Space>
        </>
      }
    </Show>
  );
};